// "INEC cancelled this unit" — a field form, not an app page. The agent may be frightened,
// one-handed and moving, so: one decision per screen, thumb-sized targets, three taps from the
// reason to Send (reason tile → Next → Send now), nothing that waits on GPS or media.
import './style.scss';

import { type FC, useEffect, useMemo, useRef, useState } from 'react';

import { useGlobalContext } from '../../../context/global';
import { useCancellationStatus } from '../../../hooks/useCancellationStatus';
import { type IEvidenceDraft, useCancellationSubmit } from '../../../hooks/useCancellationSubmit';
import type { CancellationReason } from '../../../lib/cancellation-api';
import type { ICancellationRecord } from '../../../lib/cancellation-store';
import { compressForUpload } from '../../../lib/image-compress';
import { resolveUploadCoords } from '../../../lib/upload-location';
import type { IVoiceNote } from '../../utils/voice-note/validate';
import { Receipt } from './receipt';
import { clockTime, evidenceAllowed, EyeOffGlyph, type IPuCancellationData, useDiscreetMode } from './shared';
import { ConfirmStep, EvidenceStep, evidenceSummary, type IPhoto, MAX_IMAGE_BYTES, ReasonStep } from './steps';

type Step = 'reason' | 'evidence' | 'confirm' | 'receipt';

const kindOf = (file: File): IEvidenceDraft['kind'] =>
	file.type.startsWith('video/') ? 'Video' : file.type.startsWith('audio/') ? 'Audio' : 'Image';

const PuCancellationPanel: FC = () => {
	const { rsProps, locationTracking } = useGlobalContext();
	const data = (rsProps?.data ?? {}) as Partial<IPuCancellationData>;
	const electionId = data.electionId;
	const puCode = data.pollingUnitCode;
	const [evidenceOnly, setEvidenceOnly] = useState(data.mode === 'add-evidence');

	const status = useCancellationStatus(electionId, puCode, true);
	const submitter = useCancellationSubmit(electionId, puCode);
	const [discreet, setDiscreet] = useDiscreetMode();

	const [step, setStep] = useState<Step>(() => (evidenceOnly ? 'evidence' : status.snapshot.active ? 'receipt' : 'reason'));
	const [reason, setReason] = useState<CancellationReason | null>(null);
	const [photos, setPhotos] = useState<IPhoto[]>([]);
	const [video, setVideo] = useState<File | null>(null);
	const [voice, setVoice] = useState<IVoiceNote | null>(null);
	const [note, setNote] = useState('');
	const [coords, setCoords] = useState<ICancellationRecord['coords'] | undefined>(undefined);
	const openedAt = useMemo(() => Date.now(), []);

	// GPS in the background from the moment the panel opens. Never blocks: the report sends with
	// whatever is known at the Send tap, which may be nothing.
	const trackingRef = useRef(locationTracking);
	useEffect(() => {
		let alive = true;
		void resolveUploadCoords(trackingRef.current).then((pos) => {
			if (!alive) return;
			setCoords(pos ? { latitude: pos.latitude, longitude: pos.longitude, accuracy: pos.accuracy } : null);
		});
		return () => {
			alive = false;
		};
	}, []);

	// Camera-first from the home row: those photos arrive already taken — attach them now.
	const initialPhotosRef = useRef(data.initialPhotos);
	useEffect(() => {
		const initial = initialPhotosRef.current;
		if (!initial?.length) return;
		let alive = true;
		void Promise.all(initial.map((f) => compressForUpload(f))).then((files) => {
			if (!alive) return;
			const next = files
				.filter((f) => f.size <= MAX_IMAGE_BYTES)
				.map((file) => ({ id: crypto.randomUUID(), file, url: URL.createObjectURL(file) }));
			setPhotos((prev) => [...next, ...prev]);
		});
		return () => {
			alive = false;
		};
	}, []);

	// Object URLs for photo previews die with the panel.
	const photosRef = useRef(photos);
	photosRef.current = photos;
	useEffect(() => () => photosRef.current.forEach((p) => URL.revokeObjectURL(p.url)), []);

	if (!electionId || !puCode) {
		return <p className='pc-empty'>Open this from your polling unit on the home screen.</p>;
	}

	const caps = status.caps;
	// Unknown (offline, or not asked yet) still offers capture: the files queue with the report,
	// and only a server that has said "no evidence" hides them.
	const filesSupported = evidenceAllowed(caps);

	const drafts = (): IEvidenceDraft[] => [
		...photos.map((p) => ({ kind: 'Image' as const, blob: p.file, name: p.file.name })),
		...(voice ? [{ kind: 'Audio' as const, blob: voice.blob, name: '', durationSec: voice.durationSec }] : []),
		...(video ? [{ kind: kindOf(video), blob: video, name: video.name }] : []),
	];

	const clearEvidence = () => {
		photos.forEach((p) => URL.revokeObjectURL(p.url));
		setPhotos([]);
		setVideo(null);
		setVoice(null);
	};

	const send = () => {
		if (!reason) return;
		setStep('receipt');
		const evidence = filesSupported ? drafts() : [];
		clearEvidence();
		void submitter.submit({
			reason,
			notes: note,
			pollingUnitName: data.pollingUnitName,
			evidence,
			coords: coords ?? null,
		});
	};

	const sendEvidence = () => {
		setStep('receipt');
		const evidence = drafts();
		clearEvidence();
		void submitter.addEvidence(evidence);
	};

	const quickHide = () => rsProps?.closeSection();

	const hasDraftEvidence = photos.length > 0 || !!video || !!voice;

	return (
		<div className={`pu-cancel pu-cancel--${step}${discreet ? ' pu-cancel--discreet' : ''}`}>
			<div className='pc-head'>
				<div className='pc-head__unit'>
					<span className='pc-head__code'>PU {puCode}</span>
					<span className='pc-head__name'>
						{data.pollingUnitName ? `${data.pollingUnitName} · ` : ''}
						{status.snapshot.active && step !== 'reason' ? clockTime(status.snapshot.cancelledAt || openedAt) : 'now'}
					</span>
				</div>
				<button
					type='button'
					className='pc-hide'
					onClick={quickHide}
					aria-label='Hide this screen. Sending continues.'
					title='Hide — sending continues'
				>
					<EyeOffGlyph />
				</button>
			</div>

			{step === 'reason' ? (
				<ReasonStep
					value={reason}
					onPick={(r) => {
						setReason(r);
						setStep('evidence');
					}}
				/>
			) : null}

			{step === 'evidence' ? (
				<>
					<EvidenceStep
						photos={photos}
						setPhotos={setPhotos}
						video={video}
						setVideo={setVideo}
						voice={voice}
						setVoice={setVoice}
						note={note}
						setNote={setNote}
						filesSupported={filesSupported}
						showNote={!evidenceOnly}
					/>
					<div className='pc-cta'>
						{evidenceOnly ? (
							<>
								<button
									type='button'
									className='pc-btn pc-btn--primary pc-btn--block'
									onClick={sendEvidence}
									disabled={!hasDraftEvidence || !filesSupported}
								>
									Send evidence
								</button>
								<button
									type='button'
									className='pc-btn pc-btn--ghost'
									onClick={() => setStep('receipt')}
								>
									Back
								</button>
							</>
						) : (
							<>
								<button
									type='button'
									className='pc-btn pc-btn--primary pc-btn--block'
									onClick={() => setStep('confirm')}
								>
									Next: check and send
								</button>
								<button
									type='button'
									className='pc-btn pc-btn--ghost'
									onClick={() => setStep('reason')}
								>
									Back
								</button>
							</>
						)}
					</div>
				</>
			) : null}

			{step === 'confirm' && reason ? (
				<>
					<ConfirmStep
						pollingUnitCode={puCode}
						pollingUnitName={data.pollingUnitName}
						reason={reason}
						summary={evidenceSummary(filesSupported ? photos.length : 0, filesSupported && !!voice, filesSupported && !!video)}
						note={note}
						location={coords === undefined ? 'pending' : coords ? 'ok' : 'none'}
					/>
					<div className='pc-cta'>
						<button
							type='button'
							className='pc-btn pc-btn--send pc-btn--block'
							onClick={send}
						>
							Send now
						</button>
						<button
							type='button'
							className='pc-btn pc-btn--ghost'
							onClick={() => setStep('evidence')}
						>
							Back
						</button>
					</div>
				</>
			) : null}

			{step === 'receipt' ? (
				<Receipt
					record={submitter.record ?? status.record}
					serverRow={status.serverRow}
					snapshot={status.snapshot}
					caps={caps}
					phase={submitter.phase}
					progress={submitter.progress}
					discreet={discreet}
					setDiscreet={setDiscreet}
					onRetry={() => void submitter.retry()}
					onWithdraw={async () => {
						const out = await submitter.withdraw();
						if (out.ok) status.refresh();
						return out;
					}}
					onAddEvidence={() => {
						setEvidenceOnly(true);
						setStep('evidence');
					}}
					onReattach={(id, file) => void submitter.reattach(id, { kind: kindOf(file), blob: file, name: file.name })}
					onRetryEvidence={(id) => void submitter.retryEvidence(id)}
					onDone={() => rsProps?.closeSection()}
				/>
			) : null}
		</div>
	);
};

export default PuCancellationPanel;
